import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import { AlertMessage, WithScrollbars } from '@eposnow/ui-core';
import { ScrollbarsAutoHideBehavior, ScrollbarsVisibilityBehavior } from 'overlayscrollbars';
import { UserContext } from '../../../../context/UserContext';
import FileUploader from './FileUploader';
import { CSVInput } from '../helpers/types';
import { UIContext } from '../../../../context/UIContext';

const UploadFileDialog = ({
  open,
  confirmAction,
  cancelAction,
  imageOnly = false,
}: {
  open: boolean;
  confirmAction: (file: CSVInput) => void;
  cancelAction: () => void;
  imageOnly?: boolean;
}) => {
  const { t } = useTranslation();
  const { locale } = useContext(UserContext);
  const { isMobile, colorMode } = useContext(UIContext);
  const theme = useTheme();

  const [uploadedFile, setUploadedFile] = useState<File>(undefined);
  const [warningMissingFile, setWarningMissingFile] = useState<boolean>(false);
  const [errorFileType, setErrorFileType] = useState<boolean>(false);

  const confirmButton = useCallback(
    (file) => {
      if (!file) {
        setErrorFileType(false);
        setWarningMissingFile(true);
        return;
      }
      confirmAction(file);
      setUploadedFile(undefined);
      setWarningMissingFile(false);
      setErrorFileType(false);
    },
    [confirmAction]
  );

  const cancelButton = useCallback(() => {
    cancelAction();
    setUploadedFile(undefined);
    setWarningMissingFile(false);
    setErrorFileType(false);
  }, [cancelAction]);

  useEffect(() => {
    if (!uploadedFile) return;
    setErrorFileType(false);
    setWarningMissingFile(false);
  }, [uploadedFile]);

  return (
    <Dialog
      open={open}
      onClose={cancelButton}
      aria-labelledby="upload-dialog-title"
      aria-describedby="upload-dialog-description"
      maxWidth="xl"
      sx={{
        ...(isMobile && {
          '.MuiDialog-paper': {
            margin: 0,
            maxHeight: '100%',
            height: 'inherit',
          },
        }),
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingX: '24px',
          paddingTop: '24px',
          paddingBottom: 1,
          ...(isMobile && {
            padding: 2,
          }),
        }}
      >
        <Typography variant="h2">{t('screens.products.sidekick.import.fileImportDialog.title')}</Typography>
      </Box>
      <DialogContent
        sx={{
          pr: 2,
          ...(isMobile && {
            padding: 0,
            paddingLeft: 1,
          }),
        }}
      >
        <WithScrollbars
          style={{
            height: '100%',
            minHeight: '100px',
            paddingRight: '8px',
          }}
          options={{
            scrollbars: {
              theme: colorMode === 'light' ? 'os-theme-dark' : 'os-theme-light',
              visibility: 'auto' as ScrollbarsVisibilityBehavior,
              autoHide: 'move' as ScrollbarsAutoHideBehavior,
              dragScroll: true,
              clickScroll: true,
            },
          }}
        >
          <AlertMessage type="warning" locale={locale} theme={theme} areChildrenComponents>
            <>
              <Typography fontWeight="bold">
                {t('screens.products.sidekick.import.fileImportDialog.warningCSVTitle')}
              </Typography>
              {imageOnly ? (
                <Typography>
                  - {t('screens.products.sidekick.import.fileImportDialog.warningFileFormatImage')}
                </Typography>
              ) : (
                <Typography>
                  - {t('screens.products.sidekick.import.fileImportDialog.warningFileFormat')}
                </Typography>
              )}
              <Typography>- {t('screens.products.sidekick.import.fileImportDialog.warningFileSize')}</Typography>
              {!imageOnly && (
                <Typography>
                  - {t('screens.products.sidekick.import.fileImportDialog.warningCSVFileMaximumRows')}
                </Typography>
              )}
              <Typography>- {t('screens.products.sidekick.import.fileImportDialog.warningSidekick')}</Typography>
            </>
          </AlertMessage>
          <FileUploader file={uploadedFile} setFile={setUploadedFile} imageOnly={imageOnly} />
        </WithScrollbars>
      </DialogContent>
      <DialogActions
        sx={{
          padding: '8px 24px 24px',
          ...(isMobile && {
            padding: '8px 16px 16px',
          }),
        }}
      >
        {warningMissingFile && (
          <AlertMessage type="warning" locale={locale} theme={theme}>
            {t('screens.products.sidekick.import.fileImportDialog.missingFile')}
          </AlertMessage>
        )}
        {errorFileType && (
          <AlertMessage type="error" locale={locale} theme={theme}>
            {t('screens.products.sidekick.import.fileImportDialog.errorFileType')}
          </AlertMessage>
        )}
        <Button onClick={() => cancelButton()} data-qa-id="cancel-upload-button">
          {t('actions.cancel')}
        </Button>
        <Button
          color="primary"
          variant={uploadedFile ? 'contained' : 'outlined'}
          disableElevation
          onClick={() => confirmButton(uploadedFile)}
          data-qa-id="upload-file-button"
        >
          {t('screens.products.sidekick.import.fileImportDialog.upload')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UploadFileDialog;
